import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { useJoinHousehold } from './useJoinHousehold'
import { inviteCodeSchema } from '@/shared/schemas/householdSchema'

const schema = z.object({ code: inviteCodeSchema })
type FormData = z.infer<typeof schema>

export function JoinHouseholdForm() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const joinHousehold = useJoinHousehold()

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema), mode: 'onBlur' })

  // Deep-link support: pre-fill code from URL ?invite=CODE
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const inviteCode = params.get('invite')
    if (inviteCode) {
      setValue('code', inviteCode.toUpperCase())
    }
  }, [setValue])

  const onSubmit = async (data: FormData) => {
    joinHousehold.mutate(data.code, {
      onSuccess: async () => {
        await supabase.auth.refreshSession()
        navigate({ to: '/home' })
      },
    })
  }

  const errorMessage = joinHousehold.error
    ? (joinHousehold.error as Error & { status?: number }).status === 409
      ? t('household.error.alreadyMember')
      : t('household.error.codeInvalid')
    : null

  return (
    <div className="bg-surface rounded-2xl border border-line p-8 shadow-sm">
      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-ink">{t('household.join.title')}</h2>
          <p className="mt-1 text-sm text-muted">{t('household.join.linkPrompt')}</p>
        </div>

        <div className="space-y-1">
          <label htmlFor="invite-code" className="block text-sm font-medium text-muted">
            {t('household.join.codeLabel')}
          </label>
          <input
            id="invite-code"
            type="text"
            placeholder={t('household.join.codePlaceholder')}
            className="w-full rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-ink placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition uppercase tracking-widest font-mono"
            {...register('code', {
              setValueAs: (v: string) => v.toUpperCase(),
            })}
          />
          {errors.code && (
            <span role="alert" className="block text-xs text-danger mt-1">
              {t('household.error.codeRequired')}
            </span>
          )}
        </div>

        {errorMessage && (
          <div role="alert" className="rounded-lg bg-danger/10 border border-danger/20 px-3.5 py-2.5 text-sm text-danger">
            {errorMessage}
          </div>
        )}

        <button
          type="submit"
          disabled={joinHousehold.isPending}
          className="w-full rounded-lg bg-accent text-white font-semibold py-2.5 px-4 hover:bg-accent-dark active:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-accent transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('household.join.submitButton')}
        </button>
      </form>
    </div>
  )
}
